import React, { useState, useEffect } from "react";
import InputField from "../../../Forms/Input/InputField";
import SelectField from "../../../Forms/Input/SelectField";
import SimpleSearchSelect from "../../../Forms/Input/SimpleSearchSelect";
import useIconList from "../../../../hooks/useIconList";
import { useModule } from "../../../../hooks/module/useModule";
import { useModuleCategory } from "../../../../hooks/moduleCategory/useModuleCategory";
import useDebounced from "../../../../hooks/useDebounced";

const ModuleInfoForm: React.FC = () => {
  const iconOptions = useIconList();
  const {
    moduleDetail,
    updateModuleName,
    updateModuleIcon,
    updateModuleRoute,
    updateModuleCategory,
    updateModuleHasChildren,
  } = useModule();

  // 🔹 Hook para categorías de módulos
  const { moduleCategoryDropdown, fetchModuleCategoryDropdown } = useModuleCategory();

  // 🚀 Cargar categorías al montar el componente
  useEffect(() => {
    fetchModuleCategoryDropdown();
  }, []);

  // 🕓 Si aún no hay detalle cargado
  if (!moduleDetail) {
    return (
      <div className="py-6 text-center text-gray-500 dark:text-gray-300">
        Cargando información del módulo...
      </div>
    );
  }

  // =====================================================
  // 💡 Estados locales con debounce
  // =====================================================
  const [name, setName] = useState(moduleDetail.name);
  const [route, setRoute] = useState(moduleDetail.route ?? "");

  const debouncedName = useDebounced(name, 700);
  const debouncedRoute = useDebounced(route, 700);

  // ⏱️ Lógica: Solo enviar cambios al backend cuando el valor debounced cambie
  useEffect(() => {
    if (debouncedName !== moduleDetail.name) {
      updateModuleName({
        moduleId: moduleDetail.id,
        newName: debouncedName,
      });
    }
  }, [debouncedName]);

  useEffect(() => {
    if (debouncedRoute !== moduleDetail.route && !moduleDetail.hasChildren) {
      updateModuleRoute({
        moduleId: moduleDetail.id,
        newRoute: debouncedRoute,
      });
    }
  }, [debouncedRoute]);

  // =====================================================
  // 🧩 Render UI
  // =====================================================
  return (
    <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-300 md:grid-cols-3">
      {/* 🔒 Código (solo lectura) */}
      <InputField
        label="Código"
        name="code"
        type="text"
        value={moduleDetail.code}
        iconKey="hash"
        disabled
      />

      {/* ✏️ Nombre editable con debounce */}
      <InputField
        label="Nombre"
        name="name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        iconKey="edit"
      />

      {/* 🎨 Ícono */}
      <SimpleSearchSelect
        label="Ícono del Módulo"
        name="icon"
        options={iconOptions}
        selectedValue={
          iconOptions.find((opt) => opt.name === moduleDetail.icon)?.id ?? null
        }
        onSelect={(option) =>
          updateModuleIcon({
            moduleId: moduleDetail.id,
            newIcon: option.name,
          })
        }
        searchPlaceholder="Buscar ícono..."
        iconKey="settings"
      />

      {/* 🛣️ Ruta con debounce */}
      <InputField
        label="Ruta"
        name="route"
        type="text"
        value={route}
        onChange={(e) => setRoute(e.target.value)}
        iconKey="route"
        disabled={moduleDetail.hasChildren}
        placeholder={
          moduleDetail.hasChildren
            ? "Este módulo tiene menús"
            : "Ejemplo: /ventas, /inventario"
        }
      />

      {/* 🗂️ Categoría (dinámica desde backend) */}
      <SelectField
        label="Categoría"
        name="category"
        value={moduleDetail.categoryId}
        options={moduleCategoryDropdown.map((c) => ({
          value: c.id.toString(),
          label: c.name,
        }))}
        onChange={(e) =>
          updateModuleCategory({
            moduleId: moduleDetail.id,
            categoryId: Number(e.target.value),
          })
        }
        iconKey="categories"
      />

      {/* 🌳 Submenús */}
      <SelectField
        label="Menús"
        name="hasChildren"
        value={moduleDetail.hasChildren ? "true" : "false"}
        options={[
          { value: "true", label: "Sí" },
          { value: "false", label: "No" },
        ]}
        onChange={(e) =>
          updateModuleHasChildren({
            moduleId: moduleDetail.id,
            hasChildren: e.target.value === "true",
          })
        }
        iconKey="yes"
      />
    </div>
  );
};

export default ModuleInfoForm;
